import React, { useState, useEffect, useRef } from "react";
import "./BookingHistory.css";
import { FaChevronLeft, FaChevronRight, FaHotel, FaHiking, FaCheckCircle, FaClock, FaTimesCircle, FaSearch } from "react-icons/fa";
import NavUser from "../components/NavUser";
import axios from "axios";
import PaymentService from "../services/PaymentService";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

function BookingHistory() {
  const [activeTab, setActiveTab] = useState("All");
  const [hotelBookings, setHotelBookings] = useState([]);
  const [tourBookings, setTourBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const hotelSliderRef = useRef(null);
  const guideSliderRef = useRef(null);

  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?.id;
  const BACKEND_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (!userId) return;

    const fetchData = async () => {
      try {
        const hotelRes = await axios.get(`${BACKEND_URL}/bookings/user/${userId}`);
        const tourRes = await axios.get(`${BACKEND_URL}/tour-bookings/user/${userId}`);
        
        setHotelBookings(hotelRes.data.bookings || []);
        setTourBookings(tourRes.data.bookings || []);
      } catch (err) {
        console.error("Error fetching bookings:", err);
        toast.error("Failed to load booking history");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [userId, BACKEND_URL]);

  const scroll = (ref, direction) => {
    if (ref.current) {
      const scrollAmount = 400;
      ref.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth"
      });
    }
  };

  const getStatusLabel = (statusId) => {
    switch (statusId) {
      case 1: return { label: "Confirmed", icon: <FaCheckCircle />, color: "#10b981" };
      case 2: return { label: "Pending", icon: <FaClock />, color: "#f59e0b" };
      case 3: return { label: "Cancelled", icon: <FaTimesCircle />, color: "#ef4444" };
      default: return { label: "Unknown", icon: null, color: "#64748b" };
    }
  };

  const getPaymentBadge = (b) => {
    const isPaid = b.payment_status_id === 1 || (b.paidAmount >= b.price && b.price > 0);
    const isPartial = (b.paidAmount || 0) > 0 && b.paidAmount < b.price;
    
    if (isPaid) return <span className="p-badge paid">Fully Paid</span>;
    if (isPartial) return <span className="p-badge partial">Partially Paid</span>;
    return <span className="p-badge pending">Payment Pending</span>;
  };

  const handleTopUpPayment = async (booking, type) => {
    try {
      const remainingBalance = booking.price - (booking.paidAmount || 0);
      if (remainingBalance <= 0) return;

      toast.info("Connecting to Payments.lk secure checkout...");
      const session = await PaymentService.createPaymentsLkCheckout({
        bookingType: type === "hotel" ? "hotel" : "tour",
        bookingId: booking.id,
        clientType: "web",
        isAdvance: false,
      });

      if (session && session.checkoutUrl) {
        window.location.href = session.checkoutUrl;
      } else {
        toast.error("Failed to initialize Payments.lk checkout session.");
      }
    } catch (err) {
      console.error("Top-up payment failed:", err);
      toast.error(err.response?.data?.message || err.message || "Failed to initiate top-up payment");
    }
  };

  if (isLoading) {
    return (
      <div className="agent-page-premium">
        <NavUser />
        <div className="loader-container">
          <span className="loader-dots">Fetching Your History...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="agent-page-premium">
      <NavUser />
      <ToastContainer theme="colored" />

      <div className="premium-hero-mini">
        <div className="hero-content">
          <h1>My Travel History</h1>
          <p>Relive your past adventures and manage your upcoming reservations.</p>
        </div>
      </div>

      <div className="premium-container-wide">
        <div className="history-controls">
          <div className="search-bar-v2" style={{ maxWidth: '400px' }}>
            <FaSearch />
            <input 
              type="text" 
              placeholder="Search bookings..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-pills">
            {["All", "Upcoming", "Completed", "Cancelled"].map(tab => (
              <button 
                key={tab} 
                className={`pill-btn ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Hotels Section */}
        <div className="history-section">
          <div className="section-header-v2">
            <h2><FaHotel /> Stays & Hotels</h2>
            <div className="slider-arrows">
              <button onClick={() => scroll(hotelSliderRef, "left")}><FaChevronLeft /></button>
              <button onClick={() => scroll(hotelSliderRef, "right")}><FaChevronRight /></button>
            </div>
          </div>
          
          <div className="history-slider" ref={hotelSliderRef}>
            {hotelBookings.length === 0 ? (
              <div className="empty-history-card">No hotel bookings found</div>
            ) : (
              hotelBookings.map(b => (
                <div key={b.id} className="premium-glass-card history-item-card">
                  <div className="item-status-bar">
                    <div className="status-indicator" style={{ color: getStatusLabel(b.booking_status_id).color }}>
                      {getStatusLabel(b.booking_status_id).icon}
                      <span>{getStatusLabel(b.booking_status_id).label}</span>
                    </div>
                    {getPaymentBadge(b)}
                  </div>
                  
                  <div className="item-content">
                    <h3>{b.listing?.name || "Hotel Stay"}</h3>
                    <div className="item-meta">
                      <div className="meta-pair">
                        <FaClock /> <span>{new Date(b.checking_date).toLocaleDateString()} - {new Date(b.checkout_date).toLocaleDateString()}</span>
                      </div>
                    </div>

                    <div className="payment-summary-box">
                      <div className="ps-row">
                        <span>Total Price</span>
                        <strong>Rs. {b.price?.toLocaleString()}</strong>
                      </div>
                      <div className="ps-row">
                        <span>Paid Amount</span>
                        <strong style={{ color: '#10b981' }}>Rs. {(b.paidAmount || 0).toLocaleString()}</strong>
                      </div>
                      {b.price - (b.paidAmount || 0) > 0 && (
                        <div className="ps-row balance">
                          <span>Balance Due</span>
                          <strong style={{ color: '#ef4444' }}>Rs. {(b.price - (b.paidAmount || 0)).toLocaleString()}</strong>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="item-actions">
                    <button className="btn-minimal">View Receipt</button>
                    {b.price - (b.paidAmount || 0) > 0 && (
                      <button className="btn-v2-primary mini" onClick={() => handleTopUpPayment(b, 'hotel')}>Pay Balance</button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Tours Section */}
        <div className="history-section" style={{ marginTop: '50px' }}>
          <div className="section-header-v2">
            <h2><FaHiking /> Tours & Experiences</h2>
            <div className="slider-arrows">
              <button onClick={() => scroll(guideSliderRef, "left")}><FaChevronLeft /></button>
              <button onClick={() => scroll(guideSliderRef, "right")}><FaChevronRight /></button>
            </div>
          </div>
          
          <div className="history-slider" ref={guideSliderRef}>
            {tourBookings.length === 0 ? (
              <div className="empty-history-card">No tour bookings found</div>
            ) : (
              tourBookings.map(b => (
                <div key={b.id} className="premium-glass-card history-item-card">
                  <div className="item-status-bar">
                    <div className="status-indicator" style={{ color: getStatusLabel(b.booking_status_id).color }}>
                      {getStatusLabel(b.booking_status_id).icon}
                      <span>{getStatusLabel(b.booking_status_id).label}</span>
                    </div>
                    {getPaymentBadge(b)}
                  </div>
                  
                  <div className="item-content">
                    <h3>{b.tour_listing?.title || b.tour?.name || "Tour Experience"}</h3>
                    <div className="item-meta">
                      <div className="meta-pair">
                        <FaClock /> <span>{new Date(b.booking_date).toLocaleDateString()}</span>
                      </div>
                    </div>

                    <div className="payment-summary-box">
                      <div className="ps-row">
                        <span>Total Price</span>
                        <strong>Rs. {b.price?.toLocaleString()}</strong>
                      </div>
                      <div className="ps-row">
                        <span>Paid Amount</span>
                        <strong style={{ color: '#10b981' }}>Rs. {(b.paidAmount || 0).toLocaleString()}</strong>
                      </div>
                    </div>
                  </div>

                  <div className="item-actions">
                    <button className="btn-minimal">Manage Tour</button>
                    {b.price - (b.paidAmount || 0) > 0 && (
                      <button className="btn-v2-primary mini" onClick={() => handleTopUpPayment(b, 'tour')}>Pay Balance</button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default BookingHistory;
